import { Inject, Injectable } from '@angular/core';
import { DwOrmDataService, DwOrmDataServiceToken } from '@devwareapps/devware-cap';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { AppMetaData, ChallengeTeamEntity, ChallengeTeamMemberEntity, RoDonationsByTeamEntity, UserFitnessSummaryEntity } from '../../../meta-data/app-meta-data.service';
import { TeamCardSummary, summarizeTeam } from '../models/team-card.models';
import { TeamDonationRow } from '../components/team-leaderboard/team-leaderboard.component';

import { CentralDataService } from '../../streaker-central/services/central-data.service';
import { teamProfilePath } from '../../shared/services/profile-routing/profile-links';

@Injectable({ providedIn: 'root' })
export class TeamSummaryService {
    constructor(@Inject(DwOrmDataServiceToken) private orm: DwOrmDataService, private data: CentralDataService) {}
    loadTeamDonation(challengeId: number, teamId: number): Observable<RoDonationsByTeamEntity> {
        const query = AppMetaData.RoDonationsByTeam.CreateQueryBuilder()
            .addFields(a => [a.ChallengeTeamId, a.DonationAmount])
            .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId))
            .addFilterAnd(f => f.Equal(a => a.ChallengeTeamId, teamId));
        return this.data.snapshot(`donations:team:${challengeId}:${teamId}`, () =>
            this.orm.executeQuery<RoDonationsByTeamEntity>(query).pipe(map(rows => rows.length === 1 ? rows[0] : null)));
    }
    loadDonations(challengeDefId: number, visibleTeams: ChallengeTeamEntity[]): Observable<TeamDonationRow[]> {
        if (!challengeDefId || !visibleTeams.length) { return of([]); }
        const query = AppMetaData.RoDonationsByTeam.CreateQueryBuilder()
            .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeDefId))
            .addFilterAnd(f => f.InList(a => a.ChallengeTeamId, visibleTeams.map(team => team.ChallengeTeamId)))
            .addOrderByDesc(a => a.DonationAmountPerMember)
            .addOrderBy(a => a.ChallengeTeamId);
        return this.data.snapshot(`donations:${challengeDefId}:${visibleTeams.map(team => team.ChallengeTeamId).sort((a, b) => a - b).join(",")}`, () => this.orm.executeQuery<RoDonationsByTeamEntity>(query).pipe(map(rows => rows
            .filter(row => row.DonationAmountPerMember != null && Number.isFinite(row.DonationAmountPerMember) && row.DonationAmountPerMember >= 0)
            .map(row => ({ teamId: row.ChallengeTeamId, name: row.TeamName,
                amount: row.DonationAmountPerMember, profileLink: visibleTeams.find(team => team.ChallengeTeamId === row.ChallengeTeamId)?.TeamSlug
                    ? teamProfilePath(challengeDefId, visibleTeams.find(team => team.ChallengeTeamId === row.ChallengeTeamId).TeamSlug)
                    : '/streaker-central/teams/id/' + row.ChallengeTeamId })))));
    }
    private identitiesForMembers(members: ChallengeTeamMemberEntity[]): Observable<ChallengeTeamMemberEntity[]> {
        const challengeIds = Array.from(new Set(members.map(member => member.UserChallenge?.ChallengeDefinitionId).filter(id => id > 0)));
        if (!challengeIds.length) { return of(members); }
        return combineLatest(challengeIds.map(id => this.data.challengeProfileDisplays(id, false))).pipe(take(1), map(groups => {
            const profiles = groups.reduce((all, group) => all.concat(group), []);
            return members.map(member => {
                const original = member.UserChallenge?.UserProfile;
                const identity = profiles.find(person => person.UserProfileId === member.UserChallenge?.UserProfileId
                    && (!original?.UserId || person.UserId === original.UserId));
                if (!identity) { return member; }
                const user = { ...AppMetaData.DwUser.CreateEntity(), UserId: identity.UserId, FirstName: identity.FirstName,
                    LastName: identity.LastName, UserPictureMediaId: identity.UserPictureMediaId, UserBackgroundMediaId: identity.UserBackgroundMediaId };
                return { ...member, UserChallenge: { ...member.UserChallenge, UserProfile: {
                    ...AppMetaData.UserProfile.CreateEntity(), ...identity, DwUser: user } } };
            });
        }));
    }
    load(teamIds: number[], includeActivities = true): Observable<{ [id: number]: TeamCardSummary }> {
        if (!teamIds.length) { return of({}); }
        const profile = AppMetaData.UserProfile.CreateQueryBuilder()
            .addFields(a => [a.UserProfileId, a.UserId, a.ProfileSlug, a.AppearancePresetId, a.AvatarBackgroundColor, a.AvatarTextColor, a.AvatarImageMode]);
        const enrollment = AppMetaData.UserChallenge.CreateQueryBuilder()
            .addFields(a => [a.UserChallengeId, a.UserProfileId, a.ChallengeDefinitionId])
            .addPrefetch(r => r.UserProfile, profile.query);
        const query = AppMetaData.ChallengeTeamMember.CreateQueryBuilder()
            .addFields(a => [a.ChallengeTeamId, a.UserChallengeId, a.IsTeamAdmin])
            .addFilterAnd(f => f.InList(a => a.ChallengeTeamId, teamIds))
            .addPrefetch(r => r.UserChallenge, enrollment.query);
        return this.data.snapshot(`team-summaries:${includeActivities}:${teamIds.slice().sort((a, b) => a - b).join(",")}`, () => this.orm.executeQuery<ChallengeTeamMemberEntity>(query).pipe(take(1), switchMap(members => this.identitiesForMembers(members)), switchMap(members => {
            const enrollmentIds = Array.from(new Set(members.map(member => member.UserChallengeId).filter(Boolean)));
            return (includeActivities ? this.loadMemberSummaries(enrollmentIds) : of([])).pipe(map(summaries => {
                const result: { [id: number]: TeamCardSummary } = {};
                teamIds.forEach(id => {
                    const teamMembers = members.filter(member => member.ChallengeTeamId === id);
                    const memberIds = new Set(teamMembers.map(member => member.UserChallengeId));
                    result[id] = summarizeTeam(teamMembers, summaries.filter(summary => memberIds.has(summary.UserChallengeId)));
                });
                return result;
            }));
        })));
    }
    private loadMemberSummaries(enrollmentIds: number[]): Observable<UserFitnessSummaryEntity[]> {
        if (!enrollmentIds.length) { return of([]); }
        const query = AppMetaData.UserFitnessSummary.CreateQueryBuilder()
            .addFields(a => [a.UserChallengeId, a.TotalActivities, a.TotalDurationSeconds, a.TotalMissedDays, a.LongestStreakDays])
            .addFilterAnd(f => f.InList(a => a.UserChallengeId, enrollmentIds));
        return this.orm.executeQuery<UserFitnessSummaryEntity>(query).pipe(take(1));
    }
}
